import React, { useState, useEffect, useMemo } from 'react';
import {
    X, Plus, Trash2, Edit3, Check, Search, AlertCircle, Loader2,
    Users, Flag, ChevronRight, CheckSquare, Square, ArrowRight,
    ArrowLeft, UserCheck, UserPlus, Sparkles, Filter, AlertTriangle
} from 'lucide-react';
import apiClient from '../../../utils/apiClient';
import server from '../../../utils/serverConfig';
import { getLabelStyles } from '../utils/auditHelpers';

const PRESET_COLORS = [
    { name: 'Blue', value: '#3B82F6' },
    { name: 'Emerald', value: '#10B981' },
    { name: 'Purple', value: '#8B5CF6' },
    { name: 'Amber', value: '#F59E0B' },
    { name: 'Cyan', value: '#06B6D4' },
    { name: 'Rose', value: '#F43F5E' },
    { name: 'Indigo', value: '#6366F1' }
];

export const FamilyConfigModal = ({
    isOpen,
    onClose,
    allUsers = [],
    onFamilyUpdated
}) => {
    const [familyGroups, setFamilyGroups] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);

    // Active edit state
    const [activeFamily, setActiveFamily] = useState(null); // null when in list view
    const [familyNameInput, setFamilyNameInput] = useState('');
    const [selectedColor, setSelectedColor] = useState('#3B82F6');
    const [assignedAccounts, setAssignedAccounts] = useState([]); // [{ client_id, flag: boolean, notes: string }]
    const [accountSearch, setAccountSearch] = useState('');
    const [brokerFilter, setBrokerFilter] = useState('All');
    const [memberSearch, setMemberSearch] = useState('');

    // Custom In-App Confirmation Dialog State
    const [confirmDialog, setConfirmDialog] = useState(null); // { title, message, confirmText, confirmStyle, onConfirm }

    // Fetch family groups from API
    const fetchFamilyGroups = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await apiClient.get(`${server.godseye.baseUrl}audit-performance/family-groups`);
            setFamilyGroups(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            console.error("Failed to fetch family groups", err);
            setError("Failed to load family groups. " + (err?.response?.data?.detail || err.message));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen) {
            fetchFamilyGroups();
            setActiveFamily(null);
            setError(null);
            setSuccessMessage(null);
            setConfirmDialog(null);
        }
    }, [isOpen]);

    // Map client_id -> existing family name (to show "Already in X" badges)
    const clientToFamilyMap = useMemo(() => {
        const map = {};
        familyGroups.forEach(g => {
            (g.accounts || []).forEach(acc => {
                map[acc.client_id.trim().toLowerCase()] = g.family_name;
            });
        });
        return map;
    }, [familyGroups]);

    // Distinct list of available users from allUsers + any previously assigned
    const availableUsersList = useMemo(() => {
        const list = [...allUsers];
        // Ensure SGX_JOURNAL is available
        if (!list.some(u => (u.client_id || u.clientId || '').toLowerCase() === 'sgx_journal')) {
            list.push({ client_id: 'SGX_JOURNAL', name: 'SGX Journal', broker: 'GoogleSheet', label: '-' });
        }
        return list;
    }, [allUsers]);

    // Set of currently assigned client_ids (lowercase)
    const assignedCidSet = useMemo(() => {
        return new Set(assignedAccounts.map(a => a.client_id.toLowerCase()));
    }, [assignedAccounts]);

    // Filtered selectable accounts in the left directory pool
    const filteredSelectableUsers = useMemo(() => {
        return availableUsersList.filter(u => {
            const cid = (u.client_id || u.clientId || '').toString().toLowerCase();
            const name = (u.name || '').toLowerCase();
            const label = (u.label || '').toLowerCase();
            const broker = (u.broker || '').toLowerCase();

            const matchesSearch = !accountSearch ||
                cid.includes(accountSearch.toLowerCase()) ||
                name.includes(accountSearch.toLowerCase()) ||
                label.includes(accountSearch.toLowerCase());

            const matchesBroker = brokerFilter === 'All' || broker === brokerFilter.toLowerCase();

            return matchesSearch && matchesBroker;
        });
    }, [availableUsersList, accountSearch, brokerFilter]);

    // Filtered members in the right pane
    const filteredAssignedAccounts = useMemo(() => {
        if (!memberSearch) return assignedAccounts;
        const q = memberSearch.toLowerCase();
        return assignedAccounts.filter(a => {
            const cid = a.client_id.toLowerCase();
            const userObj = availableUsersList.find(u => (u.client_id || u.clientId || '').toLowerCase() === cid);
            const name = (userObj?.name || '').toLowerCase();
            return cid.includes(q) || name.includes(q);
        });
    }, [assignedAccounts, memberSearch, availableUsersList]);

    // Start creating a new family
    const handleStartCreate = () => {
        setActiveFamily({ isNew: true });
        setFamilyNameInput('');
        setSelectedColor('#3B82F6');
        setAssignedAccounts([]);
        setAccountSearch('');
        setMemberSearch('');
        setBrokerFilter('All');
        setError(null);
        setSuccessMessage(null);
    };

    // Start editing an existing family
    const handleStartEdit = (group) => {
        setActiveFamily({ isNew: false, old_family_name: group.family_name });
        setFamilyNameInput(group.family_name);
        setSelectedColor(group.color || '#3B82F6');
        setAssignedAccounts((group.accounts || []).map(a => ({
            client_id: a.client_id,
            flag: !!a.flag,
            notes: a.notes || ''
        })));
        setAccountSearch('');
        setMemberSearch('');
        setBrokerFilter('All');
        setError(null);
        setSuccessMessage(null);
    };

    // Toggle account assignment in current active family
    const handleToggleAccount = (clientId) => {
        const cleanCid = clientId.trim();
        setAssignedAccounts(prev => {
            const exists = prev.some(a => a.client_id.toLowerCase() === cleanCid.toLowerCase());
            if (exists) {
                return prev.filter(a => a.client_id.toLowerCase() !== cleanCid.toLowerCase());
            } else {
                return [...prev, { client_id: cleanCid, flag: false, notes: '' }];
            }
        });
    };

    // Bulk Add all currently filtered accounts from directory
    const handleAddAllFiltered = () => {
        const toAdd = [];
        filteredSelectableUsers.forEach(u => {
            const cid = (u.client_id || u.clientId || '').toString().trim();
            if (cid && !assignedCidSet.has(cid.toLowerCase())) {
                toAdd.push({ client_id: cid, flag: false, notes: '' });
            }
        });
        if (toAdd.length > 0) {
            setAssignedAccounts(prev => [...prev, ...toAdd]);
        }
    };

    // Bulk remove all assigned with in-app confirmation
    const handleClearAllAssigned = () => {
        if (assignedAccounts.length === 0) return;
        setConfirmDialog({
            title: "Clear All Family Members?",
            message: `Are you sure you want to remove all ${assignedAccounts.length} accounts from "${familyNameInput || 'this family'}"? You can re-add them anytime before saving.`,
            confirmText: "Yes, Remove All",
            confirmStyle: "danger",
            onConfirm: () => {
                setAssignedAccounts([]);
                setConfirmDialog(null);
            }
        });
    };

    // Toggle flag for an assigned account
    const handleToggleAccountFlag = (clientId) => {
        setAssignedAccounts(prev =>
            prev.map(a =>
                a.client_id.toLowerCase() === clientId.toLowerCase()
                    ? { ...a, flag: !a.flag }
                    : a
            )
        );
    };

    // Save Family Group (Create or Update)
    const handleSaveFamily = async () => {
        const name = familyNameInput.trim();
        if (!name) {
            setError("Please enter a family name.");
            return;
        }

        setSaving(true);
        setError(null);
        try {
            const payload = {
                family_name: name,
                color: selectedColor,
                old_family_name: activeFamily?.isNew ? null : activeFamily?.old_family_name,
                accounts: assignedAccounts.map(a => ({
                    client_id: a.client_id,
                    flag: !!a.flag,
                    notes: a.notes || ''
                }))
            };

            await apiClient.post(`${server.godseye.baseUrl}audit-performance/family-groups`, payload);
            setSuccessMessage(`Family "${name}" saved successfully!`);
            await fetchFamilyGroups();
            if (onFamilyUpdated) onFamilyUpdated();
            setActiveFamily(null);
        } catch (err) {
            console.error("Failed to save family group", err);
            setError("Failed to save family group: " + (err?.response?.data?.detail || err.message));
        } finally {
            setSaving(false);
        }
    };

    // Delete a Family Group with in-app confirmation
    const handleDeleteFamily = (groupName) => {
        setConfirmDialog({
            title: `Delete Family "${groupName}"?`,
            message: `Are you sure you want to delete this family group? All assigned accounts will become unassigned. This action cannot be undone.`,
            confirmText: "Yes, Delete Family",
            confirmStyle: "danger",
            onConfirm: async () => {
                setConfirmDialog(null);
                setSaving(true);
                setError(null);
                try {
                    await apiClient.delete(`${server.godseye.baseUrl}audit-performance/family-groups/${encodeURIComponent(groupName)}`);
                    setSuccessMessage(`Family "${groupName}" deleted.`);
                    await fetchFamilyGroups();
                    if (onFamilyUpdated) onFamilyUpdated();
                    if (activeFamily && activeFamily.old_family_name === groupName) {
                        setActiveFamily(null);
                    }
                } catch (err) {
                    console.error("Failed to delete family group", err);
                    setError("Failed to delete family group: " + (err?.response?.data?.detail || err.message));
                } finally {
                    setSaving(false);
                }
            }
        });
    };

    if (!isOpen) return null;

    const flaggedCount = assignedAccounts.filter(a => a.flag).length;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-md p-3 sm:p-6">
            <div className="bg-[#141622] border border-gray-800/90 rounded-2xl shadow-2xl w-full max-w-6xl h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200 relative">

                {/* ─── MODAL HEADER ─── */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800 bg-[#0f111a] shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="p-2.5 rounded-xl bg-blue-500/10 border border-blue-500/25 text-blue-400">
                            <Users size={20} />
                        </div>
                        <div>
                            <h2 className="text-base font-bold text-gray-100 flex items-center gap-2">
                                Family Account Configuration Setup
                                <span className="px-2.5 py-0.5 text-[11px] bg-blue-500/20 text-blue-300 border border-blue-500/30 rounded-full font-semibold">
                                    {familyGroups.length} {familyGroups.length === 1 ? 'Family' : 'Families'}
                                </span>
                            </h2>
                            <p className="text-xs text-gray-400">
                                Organize accounts into custom family groups for aggregated PnL audits & flag key accounts
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-800/80 text-gray-400 hover:text-white rounded-xl transition-colors cursor-pointer"
                        title="Close Setup Modal"
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* ─── ALERTS ─── */}
                {error && (
                    <div className="mx-6 mt-3 p-3 bg-red-500/10 border border-red-500/30 rounded-xl flex items-center gap-2.5 text-xs text-red-400 shrink-0">
                        <AlertCircle size={16} className="shrink-0" />
                        <span>{error}</span>
                    </div>
                )}
                {successMessage && (
                    <div className="mx-6 mt-3 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-xl flex items-center gap-2.5 text-xs text-emerald-400 shrink-0">
                        <Check size={16} className="shrink-0" />
                        <span>{successMessage}</span>
                    </div>
                )}

                {/* ─── MODAL BODY ─── */}
                <div className="flex-1 overflow-hidden p-4 sm:p-6 flex flex-col">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center flex-1 text-gray-400 gap-3">
                            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                            <span className="text-sm font-medium">Loading Family Configurations...</span>
                        </div>
                    ) : activeFamily ? (
                        /* ══════════════════════════════════════════════════════════
                           SPLIT DUAL-PANE TRANSFER VIEW (CREATE / EDIT FAMILY)
                        ══════════════════════════════════════════════════════════ */
                        <div className="flex flex-col h-full gap-4">
                            {/* Top Meta Bar: Back, Name, Color & Save Actions */}
                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 bg-[#0f111a] p-3.5 rounded-xl border border-gray-800 shrink-0">
                                <div className="flex items-center gap-3 flex-1">
                                    <button
                                        type="button"
                                        onClick={() => { setActiveFamily(null); setError(null); }}
                                        className="px-3 py-1.5 text-xs text-gray-300 hover:text-white bg-gray-800/80 hover:bg-gray-700 rounded-lg transition-colors flex items-center gap-1.5 font-semibold cursor-pointer shrink-0 border border-gray-700/60"
                                    >
                                        <ArrowLeft size={13} />
                                        <span>Back</span>
                                    </button>

                                    <div className="flex items-center gap-2 flex-1 max-w-md">
                                        <input
                                            type="text"
                                            placeholder="Enter Family Name (e.g. Bafna Family)..."
                                            value={familyNameInput}
                                            onChange={(e) => setFamilyNameInput(e.target.value)}
                                            className="bg-[#181b28] border border-gray-700 focus:border-blue-500 rounded-lg px-3 py-1.5 text-xs font-semibold text-gray-100 placeholder-gray-500 outline-none transition-colors w-full"
                                            autoFocus
                                        />
                                    </div>

                                    {/* Color Swatches */}
                                    <div className="flex items-center gap-1.5 pl-2 border-l border-gray-800 shrink-0">
                                        <span className="text-[11px] text-gray-400 hidden lg:inline">Theme:</span>
                                        {PRESET_COLORS.map(c => (
                                            <button
                                                key={c.value}
                                                type="button"
                                                onClick={() => setSelectedColor(c.value)}
                                                className={`w-5 h-5 rounded-full transition-all cursor-pointer flex items-center justify-center ${
                                                    selectedColor === c.value
                                                        ? 'scale-125 ring-2 ring-white shadow-md'
                                                        : 'hover:scale-110 opacity-70 hover:opacity-100'
                                                }`}
                                                style={{ backgroundColor: c.value }}
                                                title={c.name}
                                            >
                                                {selectedColor === c.value && <Check size={10} className="text-white" />}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Right Header Actions */}
                                <div className="flex items-center gap-2 shrink-0">
                                    {!activeFamily.isNew && (
                                        <button
                                            type="button"
                                            onClick={() => handleDeleteFamily(activeFamily.old_family_name)}
                                            disabled={saving}
                                            className="px-3 py-1.5 bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/30 text-xs font-semibold rounded-lg transition-colors cursor-pointer flex items-center gap-1"
                                            title="Delete this family"
                                        >
                                            <Trash2 size={13} />
                                            <span className="hidden sm:inline">Delete</span>
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        onClick={handleSaveFamily}
                                        disabled={saving || !familyNameInput.trim()}
                                        className="px-4 py-1.5 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold rounded-lg shadow-lg shadow-blue-500/20 transition-all flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                                    >
                                        {saving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
                                        <span>Save Family</span>
                                    </button>
                                </div>
                            </div>

                            {/* Dual Pane Columns */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 flex-1 min-h-0">
                                
                                {/* ─── LEFT PANE: ACCOUNT DIRECTORY (AVAILABLE POOL) ─── */}
                                <div className="bg-[#0f111a] border border-gray-800 rounded-xl flex flex-col overflow-hidden shadow-inner">
                                    {/* Left Header */}
                                    <div className="p-3 bg-[#131520] border-b border-gray-800 flex flex-col gap-2.5">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <UserPlus size={15} className="text-blue-400" />
                                                <span className="text-xs font-bold text-gray-200 uppercase tracking-wider">
                                                    Account Directory
                                                </span>
                                                <span className="px-2 py-0.2 rounded-full text-[10px] bg-gray-800 text-gray-400 font-semibold">
                                                    {filteredSelectableUsers.length}
                                                </span>
                                            </div>

                                            {filteredSelectableUsers.length > 0 && (
                                                <button
                                                    type="button"
                                                    onClick={handleAddAllFiltered}
                                                    className="text-[11px] font-semibold text-blue-400 hover:text-blue-300 hover:underline cursor-pointer flex items-center gap-1"
                                                >
                                                    <span>+ Add All Filtered</span>
                                                </button>
                                            )}
                                        </div>

                                        {/* Filters */}
                                        <div className="flex items-center gap-2">
                                            <div className="relative flex-1">
                                                <Search size={13} className="absolute left-2.5 top-2 text-gray-500" />
                                                <input
                                                    type="text"
                                                    placeholder="Search ID, Name, Label..."
                                                    value={accountSearch}
                                                    onChange={(e) => setAccountSearch(e.target.value)}
                                                    className="w-full bg-[#181b28] border border-gray-700/80 rounded-lg pl-8 pr-7 py-1.5 text-xs text-gray-200 placeholder-gray-500 outline-none focus:border-blue-500"
                                                />
                                                {accountSearch && (
                                                    <button
                                                        type="button"
                                                        onClick={() => setAccountSearch('')}
                                                        className="absolute right-2 top-2 text-gray-500 hover:text-gray-300"
                                                    >
                                                        <X size={12} />
                                                    </button>
                                                )}
                                            </div>

                                            <select
                                                value={brokerFilter}
                                                onChange={(e) => setBrokerFilter(e.target.value)}
                                                className="bg-[#181b28] border border-gray-700/80 rounded-lg px-2.5 py-1.5 text-xs text-gray-200 outline-none focus:border-blue-500 cursor-pointer shrink-0"
                                                style={{ colorScheme: 'dark' }}
                                            >
                                                <option value="All">All Brokers</option>
                                                <option value="fyers">Fyers</option>
                                                <option value="dhan">Dhan</option>
                                                <option value="nubra">Nubra</option>
                                                <option value="zerodha">Zerodha</option>
                                                <option value="alpaca">Alpaca</option>
                                            </select>
                                        </div>
                                    </div>

                                    {/* Left Accounts List */}
                                    <div className="flex-1 overflow-y-auto p-2 divide-y divide-gray-800/40 custom-scrollbar">
                                        {filteredSelectableUsers.length === 0 ? (
                                            <div className="flex flex-col items-center justify-center py-16 text-gray-500 text-xs">
                                                <span>No matching accounts found</span>
                                            </div>
                                        ) : (
                                            filteredSelectableUsers.map(u => {
                                                const cid = (u.client_id || u.clientId || '').toString().trim();
                                                const isAssigned = assignedCidSet.has(cid.toLowerCase());
                                                const otherFamily = clientToFamilyMap[cid.toLowerCase()];
                                                const isAssignedElsewhere = otherFamily && (!activeFamily || otherFamily !== activeFamily.old_family_name);

                                                return (
                                                    <div
                                                        key={cid}
                                                        onClick={() => handleToggleAccount(cid)}
                                                        className={`flex items-center justify-between p-2 rounded-lg text-left transition-colors cursor-pointer group select-none ${
                                                            isAssigned
                                                                ? 'bg-blue-500/15 hover:bg-blue-500/20 text-blue-100 border border-blue-500/30'
                                                                : 'hover:bg-[#181b28] text-gray-300 border border-transparent'
                                                        }`}
                                                    >
                                                        <div className="flex items-center gap-2.5 overflow-hidden">
                                                            <div className={`p-1 rounded transition-colors ${isAssigned ? 'bg-blue-600 text-white' : 'text-gray-600 group-hover:text-blue-400 bg-gray-800/60'}`}>
                                                                {isAssigned ? <Check size={12} /> : <Plus size={12} />}
                                                            </div>
                                                            <div className="flex flex-col overflow-hidden">
                                                                <span className="font-mono font-bold text-xs text-gray-100 flex items-center gap-1.5">
                                                                    {cid}
                                                                    {isAssigned && (
                                                                        <span className="text-[9px] bg-blue-600/30 text-blue-300 px-1.5 py-0.2 rounded font-sans font-semibold">
                                                                            Added
                                                                        </span>
                                                                    )}
                                                                </span>
                                                                <span className="text-[11px] text-gray-400 truncate max-w-[180px]">
                                                                    {u.name || '-'}
                                                                </span>
                                                            </div>
                                                        </div>

                                                        <div className="flex items-center gap-1.5 shrink-0">
                                                            {u.label && u.label !== '-' && (
                                                                <span className={`px-1.5 py-0.2 rounded text-[8px] font-semibold uppercase ${getLabelStyles(u.label)}`}>
                                                                    {u.label}
                                                                </span>
                                                            )}
                                                            {isAssignedElsewhere && (
                                                                <span className="text-[9px] bg-purple-500/20 text-purple-300 px-1.5 py-0.2 rounded border border-purple-500/30 font-medium">
                                                                    In {otherFamily}
                                                                </span>
                                                            )}
                                                            {u.broker && (
                                                                <span className="text-[9px] text-gray-500 uppercase font-mono">
                                                                    {u.broker}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })
                                        )}
                                    </div>
                                </div>

                                {/* ─── RIGHT PANE: FAMILY MEMBERS & FLAG CONTROLS ─── */}
                                <div className="bg-[#0f111a] border border-gray-800 rounded-xl flex flex-col overflow-hidden shadow-inner">
                                    {/* Right Header */}
                                    <div className="p-3 bg-[#131520] border-b border-gray-800 flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <span
                                                className="w-3 h-3 rounded-full shrink-0 shadow-sm"
                                                style={{ backgroundColor: selectedColor || '#3B82F6' }}
                                            />
                                            <span className="text-xs font-bold text-gray-200 uppercase tracking-wider">
                                                Family Members ({assignedAccounts.length})
                                            </span>
                                            {flaggedCount > 0 && (
                                                <span className="px-2 py-0.5 rounded-full text-[10px] bg-amber-500/20 text-amber-300 border border-amber-500/30 font-bold flex items-center gap-1">
                                                    <Flag size={10} className="fill-amber-400" />
                                                    {flaggedCount} Flagged
                                                </span>
                                            )}
                                        </div>

                                        {assignedAccounts.length > 0 && (
                                            <button
                                                type="button"
                                                onClick={handleClearAllAssigned}
                                                className="text-[11px] font-semibold text-red-400 hover:text-red-300 hover:underline cursor-pointer"
                                            >
                                                Clear All
                                            </button>
                                        )}
                                    </div>

                                    {/* Quick Search inside members if list is large */}
                                    {assignedAccounts.length > 6 && (
                                        <div className="px-3 pt-2.5 pb-1 bg-[#131520]/50 border-b border-gray-800/60">
                                            <div className="relative">
                                                <Search size={12} className="absolute left-2.5 top-2 text-gray-500" />
                                                <input
                                                    type="text"
                                                    placeholder="Filter family members..."
                                                    value={memberSearch}
                                                    onChange={(e) => setMemberSearch(e.target.value)}
                                                    className="w-full bg-[#181b28] border border-gray-800 rounded-lg pl-8 pr-7 py-1 text-xs text-gray-200 placeholder-gray-500 outline-none focus:border-blue-500"
                                                />
                                                {memberSearch && (
                                                    <button
                                                        type="button"
                                                        onClick={() => setMemberSearch('')}
                                                        className="absolute right-2 top-1.5 text-gray-500 hover:text-gray-300"
                                                    >
                                                        <X size={12} />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {/* Members List */}
                                    <div className="flex-1 overflow-y-auto p-2 divide-y divide-gray-800/40 custom-scrollbar">
                                        {assignedAccounts.length === 0 ? (
                                            <div className="flex flex-col items-center justify-center py-20 text-gray-500 text-xs text-center px-6 gap-2">
                                                <UserPlus size={28} className="text-gray-600 opacity-60" />
                                                <span className="font-semibold text-gray-400">No accounts in this family yet</span>
                                                <span className="text-[11px] text-gray-500">
                                                    Click any account from the Directory on the left to add it here.
                                                </span>
                                            </div>
                                        ) : filteredAssignedAccounts.length === 0 ? (
                                            <div className="flex flex-col items-center justify-center py-12 text-gray-500 text-xs">
                                                <span>No members match filter</span>
                                            </div>
                                        ) : (
                                            filteredAssignedAccounts.map((acc, idx) => {
                                                const userObj = availableUsersList.find(u =>
                                                    (u.client_id || u.clientId || '').toLowerCase() === acc.client_id.toLowerCase()
                                                );

                                                return (
                                                    <div
                                                        key={acc.client_id}
                                                        className={`flex items-center justify-between p-2.5 rounded-lg transition-all ${
                                                            acc.flag
                                                                ? 'bg-amber-500/10 border border-amber-500/30 text-amber-200'
                                                                : 'hover:bg-[#181b28] text-gray-200 border border-transparent'
                                                        }`}
                                                    >
                                                        <div className="flex items-center gap-2.5 overflow-hidden">
                                                            <span className="text-[10px] text-gray-500 font-mono w-4 text-right">
                                                                {idx + 1}
                                                            </span>
                                                            <div className="flex flex-col overflow-hidden">
                                                                <span className="font-mono font-bold text-xs text-gray-100 flex items-center gap-1.5">
                                                                    {acc.client_id}
                                                                    {userObj?.label && userObj.label !== '-' && (
                                                                        <span className={`px-1.5 py-0.2 rounded text-[8px] font-semibold uppercase ${getLabelStyles(userObj.label)}`}>
                                                                            {userObj.label}
                                                                        </span>
                                                                    )}
                                                                </span>
                                                                <span className="text-[11px] text-gray-400 truncate max-w-[200px]">
                                                                    {userObj?.name || '-'}
                                                                </span>
                                                            </div>
                                                        </div>

                                                        {/* Actions: Flag & Remove */}
                                                        <div className="flex items-center gap-2 shrink-0">
                                                            {/* 1-Click Boolean Flag Button */}
                                                            <button
                                                                type="button"
                                                                onClick={() => handleToggleAccountFlag(acc.client_id)}
                                                                className={`px-2 py-1 rounded-md transition-all cursor-pointer flex items-center gap-1 text-[10px] font-bold ${
                                                                    acc.flag
                                                                        ? 'bg-amber-500/30 text-amber-300 border border-amber-500/50 shadow-sm shadow-amber-500/20'
                                                                        : 'bg-gray-800/80 hover:bg-gray-700 text-gray-400 hover:text-amber-400 border border-gray-700/60'
                                                                }`}
                                                                title={acc.flag ? "Flag: True (Click to set False)" : "Flag: False (Click to set True)"}
                                                            >
                                                                <Flag size={11} className={acc.flag ? 'fill-amber-400 text-amber-400' : ''} />
                                                                <span>{acc.flag ? 'Flagged' : 'Flag'}</span>
                                                            </button>

                                                            {/* Remove Button */}
                                                            <button
                                                                type="button"
                                                                onClick={() => handleToggleAccount(acc.client_id)}
                                                                className="p-1 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors cursor-pointer"
                                                                title="Remove from family"
                                                            >
                                                                <X size={14} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                );
                                            })
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        /* ══════════════════════════════════════════════════════════
                           FAMILIES DASHBOARD GRID VIEW
                        ══════════════════════════════════════════════════════════ */
                        <div className="flex flex-col gap-5 h-full overflow-y-auto custom-scrollbar pr-1">
                            {/* Dashboard Top Toolbar */}
                            <div className="flex items-center justify-between shrink-0">
                                <div>
                                    <h3 className="text-sm font-bold text-gray-100">
                                        Configured Family Groups
                                    </h3>
                                    <p className="text-xs text-gray-400">
                                        Create and manage family groups for group PnL audit reports
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={handleStartCreate}
                                    className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold rounded-xl shadow-lg shadow-blue-500/20 transition-all flex items-center gap-1.5 cursor-pointer"
                                >
                                    <Plus size={15} />
                                    <span>Create New Family</span>
                                </button>
                            </div>

                            {/* Families Cards Grid */}
                            {familyGroups.length === 0 ? (
                                <div className="flex flex-col items-center justify-center flex-1 py-16 text-gray-500 border border-dashed border-gray-800 rounded-2xl bg-[#0f111a]/60 gap-3">
                                    <div className="p-4 rounded-2xl bg-blue-500/10 text-blue-400">
                                        <Users size={36} />
                                    </div>
                                    <div className="text-center">
                                        <h4 className="text-sm font-bold text-gray-200">No Family Groups Configured</h4>
                                        <p className="text-xs text-gray-500 mt-1 max-w-sm">
                                            Group client accounts together to view aggregated family yield and net PnL in the performance modals.
                                        </p>
                                    </div>
                                    <button
                                        onClick={handleStartCreate}
                                        className="mt-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold rounded-lg shadow transition-all cursor-pointer flex items-center gap-1.5"
                                    >
                                        <Plus size={14} />
                                        <span>Create Your First Family</span>
                                    </button>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {familyGroups.map(group => {
                                        const flCount = (group.accounts || []).filter(a => a.flag).length;
                                        return (
                                            <div
                                                key={group.family_name}
                                                className="bg-[#0f111a] border border-gray-800 hover:border-gray-700 rounded-2xl p-4 flex flex-col justify-between transition-all hover:shadow-xl hover:shadow-black/40 group relative overflow-hidden"
                                            >
                                                {/* Color accent line */}
                                                <div
                                                    className="absolute top-0 left-0 right-0 h-1"
                                                    style={{ backgroundColor: group.color || '#3B82F6' }}
                                                />

                                                <div>
                                                    <div className="flex items-center justify-between mb-2.5 pt-1">
                                                        <div className="flex items-center gap-2 overflow-hidden">
                                                            <span
                                                                className="w-3 h-3 rounded-full shrink-0 shadow-sm"
                                                                style={{ backgroundColor: group.color || '#3B82F6' }}
                                                            />
                                                            <h4 className="font-bold text-sm text-gray-100 truncate" title={group.family_name}>
                                                                {group.family_name}
                                                            </h4>
                                                        </div>

                                                        <div className="flex items-center gap-1 opacity-80 group-hover:opacity-100 shrink-0">
                                                            <button
                                                                onClick={() => handleStartEdit(group)}
                                                                className="p-1.5 hover:bg-gray-800 text-gray-400 hover:text-blue-400 rounded-lg transition-colors cursor-pointer"
                                                                title="Edit Family"
                                                            >
                                                                <Edit3 size={14} />
                                                            </button>
                                                            <button
                                                                onClick={() => handleDeleteFamily(group.family_name)}
                                                                className="p-1.5 hover:bg-gray-800 text-gray-400 hover:text-red-400 rounded-lg transition-colors cursor-pointer"
                                                                title="Delete Family"
                                                            >
                                                                <Trash2 size={14} />
                                                            </button>
                                                        </div>
                                                    </div>

                                                    {/* Stats Badges */}
                                                    <div className="flex items-center gap-2 mb-3">
                                                        <span className="text-xs bg-gray-800/90 text-gray-300 px-2.5 py-0.5 rounded-md font-semibold">
                                                            {group.accounts?.length || 0} accounts
                                                        </span>
                                                        {flCount > 0 && (
                                                            <span className="text-xs bg-amber-500/15 text-amber-300 border border-amber-500/30 px-2 py-0.5 rounded-md font-semibold flex items-center gap-1">
                                                                <Flag size={10} className="fill-amber-400" />
                                                                {flCount} flagged
                                                            </span>
                                                        )}
                                                    </div>

                                                    {/* Member Accounts Preview Pills */}
                                                    <div className="flex flex-wrap gap-1.5 max-h-24 overflow-hidden">
                                                        {(group.accounts || []).slice(0, 8).map(a => (
                                                            <span
                                                                key={a.client_id}
                                                                className={`px-2 py-0.5 rounded text-[10px] font-mono font-medium border ${
                                                                    a.flag
                                                                        ? 'bg-amber-500/15 border-amber-500/40 text-amber-300'
                                                                        : 'bg-[#181b28] border-gray-800 text-gray-300'
                                                                }`}
                                                            >
                                                                {a.client_id}
                                                                {a.flag && ' ⭐'}
                                                            </span>
                                                        ))}
                                                        {(group.accounts || []).length > 8 && (
                                                            <span className="px-2 py-0.5 text-[10px] text-gray-500 font-medium">
                                                                +{(group.accounts || []).length - 8} more
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Manage CTA Button */}
                                                <button
                                                    onClick={() => handleStartEdit(group)}
                                                    className="mt-4 pt-3 border-t border-gray-800/80 text-xs font-bold text-blue-400 hover:text-blue-300 flex items-center justify-between cursor-pointer group-hover:text-blue-300"
                                                >
                                                    <span>Manage Members & Flags</span>
                                                    <ChevronRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
                                                </button>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* ─── CUSTOM IN-APP CONFIRMATION POPUP DIALOG ─── */}
                {confirmDialog && (
                    <div
                        className="fixed inset-0 z-60 flex items-center justify-center bg-black/75 backdrop-blur-sm p-4 animate-in fade-in duration-150"
                        onClick={() => setConfirmDialog(null)}
                    >
                        <div
                            className="bg-[#171926] border border-gray-700/80 rounded-2xl shadow-2xl max-w-md w-full p-5 text-gray-200 animate-in zoom-in-95 duration-150 flex flex-col gap-4"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="flex items-start gap-3.5">
                                <div className={`p-2.5 rounded-xl shrink-0 ${
                                    confirmDialog.confirmStyle === 'danger'
                                        ? 'bg-red-500/15 border border-red-500/30 text-red-400'
                                        : 'bg-amber-500/15 border border-amber-500/30 text-amber-400'
                                }`}>
                                    <AlertTriangle size={22} />
                                </div>
                                <div className="flex-1">
                                    <h3 className="text-sm font-bold text-gray-100">
                                        {confirmDialog.title}
                                    </h3>
                                    <p className="text-xs text-gray-400 mt-1 leading-relaxed">
                                        {confirmDialog.message}
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-center justify-end gap-2.5 pt-2 border-t border-gray-800/80">
                                <button
                                    type="button"
                                    onClick={() => setConfirmDialog(null)}
                                    className="px-3.5 py-1.5 bg-gray-800/90 hover:bg-gray-700 text-gray-300 text-xs font-semibold rounded-lg transition-colors cursor-pointer border border-gray-700/60"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={confirmDialog.onConfirm}
                                    className={`px-4 py-1.5 text-xs font-bold rounded-lg shadow-lg transition-all cursor-pointer flex items-center gap-1.5 ${
                                        confirmDialog.confirmStyle === 'danger'
                                            ? 'bg-red-600 hover:bg-red-500 text-white shadow-red-600/20'
                                            : 'bg-amber-600 hover:bg-amber-500 text-white shadow-amber-600/20'
                                    }`}
                                >
                                    {confirmDialog.confirmText || 'Confirm'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default FamilyConfigModal;
